import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { cancel, schedule, throttle } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { getHeightWithoutPaddings, getWidthWithoutPaddings } from 'webapp/utils/html';

import highlightResult from 'webapp/helpers/highlight-result';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { TrackedEvent } from 'webapp/services/analytics';

// a model that can be edited (canEdit) saves itself
interface SavableModel {
	save(): Promise<unknown>;
}

interface Args {
	model: object; // the text is its editableProperty
	canEdit?: boolean;
	editableProperty?: string;
	trackingObj?: TrackedEvent;
	className?: string;
	hideTooltip?: boolean;
	skipBESave?: boolean;
	allowEmpty?: boolean;
	maxlength?: number;
	iconSize?: IconSize;
}

interface EditableModelPropertyComponentInterface<T> {
	Args: T;
	Blocks: { default: [] }; // this is needed for yield
}

export default class EditableModelPropertyComponent extends Component<
	EditableModelPropertyComponentInterface<Args>
> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('request') declare requestService: Service.Request;

	@tracked newText = '';
	@tracked isInlineEditingActive = false;
	@tracked isTextTooLong = false;

	name: string;
	node: HTMLElement | null = null;
	resizeObserver: ResizeObserver | null = null;
	resizeCheckTimer?: ReturnType<typeof throttle>;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.name = this.editableProperty;
	}

	get classNames(): string {
		const mainClassName = 'editable-text';
		const classNames = [mainClassName];

		classNames.push(`${mainClassName}-${this.args.className || 'title'}`);

		if (!this.text.length) {
			classNames.push(`${mainClassName}--blank`);
		}

		if (this.isInlineEditingActive) {
			classNames.push(`${mainClassName}--active`);
		}

		if (this.args.canEdit) {
			classNames.push(`${mainClassName}--can-edit`);
		}

		return classNames.join(' ');
	}

	get iconSize(): IconSize {
		return this.args.iconSize || 'xs';
	}

	get maxlength() {
		return this.args.maxlength || 50;
	}

	get editableProperty(): string {
		return this.args.editableProperty || 'title';
	}

	get text(): string {
		return (Reflect.get(this.args.model, this.editableProperty) as string | undefined) || '';
	}

	get highlightMatch() {
		return Reflect.get(this.args.model, 'activeFilter') as string | string[] | undefined;
	}

	get isTooltipShown(): boolean {
		if (this.args.hideTooltip) {
			return false;
		}
		return this.isTextTooLong;
	}

	private previousTextChecked = '';

	checkIfTextTooLong(): void {
		const node = this.node,
			pseudoElementWidth = 16, // margin left and pencil icon (4px + 12px) - search by @pseudoElementWidth in less (pseudo element is used to show pencil right behind last character)
			buttonDivNode = node?.querySelector('div[role="button"]'),
			textContainerHeight = Math.floor(getHeightWithoutPaddings(node)),
			textContainerWidth = Math.floor(getWidthWithoutPaddings(node)),
			textHeight = node ? Math.round(buttonDivNode?.scrollHeight || 0) : 0,
			textWidth = node ? Math.round(buttonDivNode?.scrollWidth || 0) - pseudoElementWidth : 0,
			labelNode = node?.querySelector('.editable-text__label'),
			isLabelTruncated = !!labelNode && labelNode.scrollWidth > labelNode.clientWidth;

		this.isTextTooLong =
			textHeight > textContainerHeight || textWidth > textContainerWidth || isLabelTruncated;

		this.previousTextChecked = this.text;
	}

	@action
	startEditing(): void {
		if (this.args.canEdit) {
			this.newText = this.text;
			this.isInlineEditingActive = true;
		}
	}

	@action
	stopEditing(): void {
		const trackingObj = this.args.trackingObj;
		const newText = this.newText.trim();
		const allowEmpty = this.args.allowEmpty;

		if ((newText || allowEmpty) && newText !== this.text) {
			Reflect.set(this.args.model, this.editableProperty, newText);

			if (this.args.skipBESave) {
				return;
			}

			(this.args.model as SavableModel)
				.save()
				.then(() => {
					if (trackingObj) {
						this.analyticsService.event(trackingObj.event, trackingObj.data);
					}
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}

		this.isInlineEditingActive = false;
	}

	@action
	scheduleCheckIfTextTooLong(): void {
		if (this.text !== this.previousTextChecked) {
			schedule('afterRender', this, this.checkIfTextTooLong);
		}
	}

	@action
	setupView(node: HTMLElement): void {
		this.node = node;

		this.resizeObserver = new ResizeObserver(() => {
			this.resizeCheckTimer = throttle(this, this.checkIfTextTooLong, 200, false);
		});
		this.resizeObserver.observe(node);

		this.scheduleCheckIfTextTooLong();
	}

	override willDestroy(): void {
		super.willDestroy();
		this.resizeObserver?.disconnect();
		cancel(this.resizeCheckTimer);
	}
	<template>
		<div
			class={{this.classNames}}
			{{didInsert this.setupView}}
			{{didUpdate this.scheduleCheckIfTextTooLong this.text}}
		>
			{{#if this.isInlineEditingActive}}
				<FormInputComponent
					name={{this.name}}
					maxlength={{this.maxlength}}
					@value={{this.newText}}
					@autofocus={{true}}
					@loseFocusOnEnter={{true}}
					@focusOut={{this.stopEditing}}
				/>
			{{else}}
				<div role="button" {{on "click" this.startEditing}}>
					<span class="editable-text__label">
						{{highlightResult text=this.text match=this.highlightMatch}}
					</span><CommonUiIconComponent
						@name="Pencil"
						@size={{this.iconSize}}
						@class="pencil-icon"
					/>
				</div>

				{{#if this.isTooltipShown}}
					<ToolTipComponent @text={{this.text}} @maxWidth={{250}} />
				{{/if}}

				{{yield}}
			{{/if}}
		</div>
	</template>
}
